import "./App.css";

import * as _ from "lodash";

import {
  ANY_SEASON,
  AUTUMN,
  MAX_NUM_OF_PLANTS_PER_COMBINATION,
  SPRING,
  SUMMER,
  WINTER,
  seasonOr,
} from "../../generator/consts";
import { MinMax, useMinMax } from "./MinMax";
import { PlantName, plants, uselessVeggies } from "../../generator/config";
import React, { useState } from "react";
import { useBoolean, useElementSize, useWindowSize } from "usehooks-ts";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FixedSizeList as List } from "react-window";
import { Plant } from "../../generator/model";
import { PlantSelect } from "../PlantSelect/PlantSelect";
import { Recipe } from "../Recipe/Recipe";
import Select from "react-select";
import { customStyles } from "../../react-select-styles";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { useCookies } from "react-cookie";
import { usePinnedRecipees } from "../cookies";
import { useQuery } from "react-query";
import { useWorker } from "../../workers/useWorker";

const absSum = (vals: number[]) => _.sum(vals.map((n) => Math.abs(n)));
const listPadding = 10;

const getRelativePlantRatio = (list: Plant[], target: Plant): number => {
  const size = list.length;
  let count = 0;
  list.forEach((plant) => (count += plant.name === target.name ? 1 : 0));

  return count / size;
};

const RecipeRow = (props: {
  index: number;
  style: React.CSSProperties;
  data: { recipeeList: Plant[][]; numPinned: number };
}) => {
  const { index, style, data } = props;
  const { recipeeList } = data;
  return (
    <div style={style}>
      <Recipe plants={recipeeList[index]} />
    </div>
  );
};

export function App() {
  const worker = useWorker();

  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [filterAreaRef, { height: filterAreaHeight }] = useElementSize();
  const [selectedSeasons, setSelectedSeasons] = useState(0);
  const [selectedSizes, setSelectedSizes] = useState(
    _.range(1, MAX_NUM_OF_PLANTS_PER_COMBINATION)
  );

  const [usedPlants, setUsedPlants] = useState([] as Plant[]);
  const [avoidedPlants, setAvoidedPlants] = useState([] as Plant[]);
  const usedPlantNames = usedPlants.map((p) => p.name);
  const avoidedPlantNames = avoidedPlants.map((p) => p.name);
  const [recipees, setRecipees] = React.useState([] as Plant[][]);
  const pinnedRecipees = usePinnedRecipees();
  const filterDuplicates = useBoolean(true);

  const [maxWaterConsumption, setMaxWaterConsumption] = React.useState(27);
  const minMaxManure = useMinMax({ initialValue: ["0", "0"] });
  const minMaxCompost = useMinMax({ initialValue: ["0", "0"] });
  const minMaxFormula = useMinMax({ initialValue: ["0", "0"] });

  const allFertilizersOnZero =
    absSum([
      ...minMaxCompost.range,
      ...minMaxManure.range,
      ...minMaxFormula.range,
    ]) === 0;
  const compostIsZero = minMaxCompost.rangeRaw.some((d) => d === "");
  const manureIsZero = minMaxManure.rangeRaw.some((d) => d === "");
  const formulaIsZero = minMaxFormula.rangeRaw.some((d) => d === "");

  const queryParams: Parameters<typeof worker.generateRecipees>[0] = {
    withFertilizerYield: {
      manure: manureIsZero ? null : minMaxManure.range,
      compost: compostIsZero ? null : minMaxCompost.range,
      growthFormula: formulaIsZero ? null : minMaxFormula.range,
    },
    recipeeLengths: selectedSizes,
    onlyUnique: filterDuplicates.value,
    plantBlacklist: avoidedPlants.map(
      (plant) => plant.name as any as PlantName
    ),
    seasonWhitelistMask: selectedSeasons || ANY_SEASON,
  };

  const queryGenerator = useQuery(
    ["workerGenRecipees", queryParams],
    async () => {
      return await worker.generateRecipees(queryParams);
    },
    { staleTime: Number.MAX_SAFE_INTEGER }
  );
  React.useEffect(() => {
    setRecipees(queryGenerator.data ?? []);
  }, [queryGenerator.data]);

  let filteredRecipees = React.useMemo(
    () =>
      recipees.filter((recipe) => {
        const names = recipe.map((p) => p.name);
        const waterConsumption = _.sum(recipe.map((d) => d.water));

        return (
          // only show recipees containing all of the selected plants
          (usedPlants.length === 0 ||
            usedPlants.every((plant) => names.includes(plant.name))) &&
          waterConsumption <= maxWaterConsumption
        );
      }),
    [
      recipees,
      selectedSizes,
      usedPlants,
      selectedSeasons,
      avoidedPlantNames,
      maxWaterConsumption,
    ]
  );

  if (!!usedPlants.length) {
    filteredRecipees = filteredRecipees.map((plants) => {
      // make sure the selected items are listed first in each row
      // this makes it easyer to gauge the "relative selected plant ratio"
      const selectedItems = [] as Plant[];
      const otherItems = [] as Plant[];
      plants.forEach((plant) =>
        usedPlantNames.includes(plant.name)
          ? selectedItems.push(plant)
          : otherItems.push(plant)
      );
      return [...selectedItems, ...otherItems];
    });

    filteredRecipees = _.orderBy(
      filteredRecipees,
      [
        (recipe) =>
          usedPlants.reduce(
            (sum, next) => sum + getRelativePlantRatio(recipe, next),
            0
          ),
        (recipe) => recipe.length,
      ],
      ["desc", "asc"]
    );
  }

  const recipeeList = [[], ...pinnedRecipees.get(), ...filteredRecipees];

  const configHeight = filterAreaHeight || 200;

  return (
    <div>
      <div className="container-fluid" ref={filterAreaRef}>
        <div className="row md-1">
          {/* Size */}
          <div className="col col-md-4">
            <Select
              placeholder="Sizes..."
              options={_.range(2, MAX_NUM_OF_PLANTS_PER_COMBINATION + 1).map(
                (i) => ({
                  label: i,
                  value: i,
                })
              )}
              value={selectedSizes.map((i) => ({
                label: i,
                value: i,
              }))}
              isMulti={true}
              onChange={(options) => {
                setSelectedSizes(options.map((o) => o.value));
              }}
              styles={customStyles}
            />
          </div>

          {/* Season */}
          <div className="col col-md-4">
            <Select
              placeholder={"Season..."}
              options={[
                { label: "Autumn", value: AUTUMN },
                { label: "Winter", value: WINTER },
                { label: "Spring", value: SPRING },
                { label: "Summer", value: SUMMER },
              ]}
              isMulti={true}
              onChange={(options) => {
                setSelectedSeasons(seasonOr(options.map((o) => o.value)));
              }}
              styles={customStyles}
            />
          </div>

          {/* Water */}
          <div className="col col-md-2">
            <div className="input-group mb-3">
              <span className="input-group-text">Max Water</span>
              <input
                onFocus={(e) => e.target.select()}
                className="form-control"
                type="number"
                value={maxWaterConsumption}
                onChange={(e) => {
                  setMaxWaterConsumption(Number.parseInt(e.target.value));
                }}
              />
            </div>
          </div>

          {/* Info */}
          <div className="col col-md-2" style={{ textAlign: "end" }}>
            {queryGenerator.isLoading ? (
              <span>Loading...</span>
            ) : (
              <span>
                Found <b>{filteredRecipees.length}</b>{" "}
                {filterDuplicates.value ? "distinct" : ""} Recipees
              </span>
            )}
          </div>
        </div>
        <div className="row md-1">
          {/* Manure Range */}
          <div className="col col-md-2">
            <MinMax label="Manure" state={minMaxManure} />
          </div>

          {/* Compost Range */}
          <div className="col col-md-2">
            <MinMax label="Compost" state={minMaxCompost} />
          </div>

          {/* Growth Formula Range */}
          <div className="col col-md-3">
            <MinMax label="Growth Formula" state={minMaxFormula} />
          </div>

          {/* Toggle Duplicates */}
          <div className="form-check form-switch col col-md-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="flexSwitchCheckDefault"
              checked={filterDuplicates.value}
              onClick={filterDuplicates.toggle}
            />
            <label
              className="form-check-label"
              htmlFor="flexSwitchCheckDefault"
            >
              Filter Reduntant Combos
            </label>
          </div>

          {/* Include */}
          <div className="row md-1">
            <div className="col col-md-1 align-self-center row-label">
              Include:
            </div>
            <div className="col col-md-11">
              <PlantSelect
                list={plants.map((plant) => ({ plant, isSelected: false }))}
                highlightColor={"#00A120"}
                onChange={(selected) => {
                  setUsedPlants(
                    selected
                      .filter((opt) => opt.isSelected)
                      .map((opt) => opt.plant)
                  );
                }}
              />
            </div>
          </div>

          {/* Exclude */}
          <div className="row md-1">
            <div className="col col-md-1 align-self-center row-label">
              Exclude:
            </div>
            <div className="col col-md-11">
              <PlantSelect
                list={plants.map((plant) => ({
                  plant,
                  isSelected: uselessVeggies.includes(plant),
                }))}
                highlightColor={"#A10400"}
                onChange={React.useCallback(
                  (selected) =>
                    setAvoidedPlants(
                      selected
                        .filter((opt) => opt.isSelected)
                        .map((opt) => opt.plant)
                    ),
                  []
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          left: listPadding,
          right: listPadding,
          bottom: listPadding,
          boxShadow: "0px 0px 5px black",
        }}
      >
        <List
          width={(windowWidth || 0) - listPadding}
          height={(windowHeight || 0) - configHeight - 2 * listPadding}
          itemSize={70}
          itemCount={recipeeList.length}
          itemData={{ recipeeList } as any}
        >
          {RecipeRow}
        </List>
      </div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: listPadding + 18,
          top: configHeight,
          backgroundColor: "white",
        }}
      >
        <div style={{ height: 70 }}>
          <Recipe
            plants={_.flatten(pinnedRecipees.get())}
            isPinnedRow
            isTotal
          />
        </div>
        {pinnedRecipees.get().map((plants) => (
          <div style={{ height: 70 }}>
            <Recipe plants={plants} isPinnedRow />
          </div>
        ))}
      </div>
      <div
        style={{
          position: "absolute",
          right: listPadding + 18,
          top: configHeight,
          zIndex: 10,
          padding: 14,
        }}
      >
        <button
          type="button"
          className="btn btn-warning"
          onClick={pinnedRecipees.clear}
        >
          Clear Pinned <FontAwesomeIcon icon={faTrash} size={"1x"} fixedWidth />
        </button>
      </div>
    </div>
  );
}
